import React, { useState, useEffect, useCallback } from "react";
import { Box, Stack, Typography, Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, TablePagination, Chip, IconButton, Grid, TextField, Autocomplete, Button, Card, CardContent, Collapse, Tooltip, Avatar, AvatarGroup, Dialog, DialogTitle, DialogContent } from "@mui/material";
import { Search as SearchIcon, RestartAlt as ResetIcon, Assignment as TaskIcon, PendingActions as ProgressIcon, CheckCircle as CompletedIcon, RadioButtonUnchecked as OpenIcon, FilterList as FilterIcon, ExpandLess as ExpandLessIcon, RateReview, PendingActions, Visibility, Add as AddIcon, Close as CloseIcon, Folder } from '@mui/icons-material';
import { useNavigate } from "react-router-dom";
import { getData } from "../../../../services/axiosInstance";
import { API_ENDPOINTS } from "../../../../services/endpoints";
import Loader from "../../../../common/spinner";
import { getStatusColor, priorityColors, issueTypeColors } from "../../../../utils/statusColor";
import { formatDate } from "../../../../utils/dateFormat";
import CreateTask from "./createTask";

const StatCard = ({ title, value, icon, color }) => (
	<Card
		sx={{
			borderRadius: '16px',
			boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
			height: '100%',
			background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
			color: '#FFFFFF',
			position: 'relative',
			overflow: 'hidden'
		}}
	>
		<CardContent sx={{ position: 'relative', zIndex: 1 }}>
			<Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
				<Box>
					<Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '600', opacity: 0.9 }}>
						{title}
					</Typography>
					<Typography variant="h4" fontWeight="800" sx={{ mt: 0.5 }}>
						{value}
					</Typography>
				</Box>
				<Box sx={{ bgcolor: 'rgba(25, 25, 25, 0.2)', p: 1.5, borderRadius: '12px', display: 'flex', backdropFilter: 'blur(4px)' }}>
					{React.cloneElement(icon, { sx: { color: '#FFFFFF', fontSize: 28 } })}
				</Box>
			</Stack>
		</CardContent>
		<Box sx={{ position: 'absolute', top: '-20%', right: '-10%', width: '120px', height: '120px', borderRadius: '50%', background: 'rgba(255, 255, 255, 0.1)', zIndex: 0 }} />
	</Card>
);

function TaskManagement() {
	const navigate = useNavigate();
	const userDataRaw = localStorage.getItem('userData');
	const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
	const userId = userData?.id;
	const userRole = userData?.user_type;
	const roleBasePath = userRole === 'Team Lead' ? '/team-lead' : '/manager';
	const orgId = userData?.organization_id;
	const [statCount, setStatCounts] = useState({});
	const [employees, setEmployees] = useState([]);
	const [state, setState] = useState({
		isMyView: false,
		page: 0,
		limit: 10,
		tasks: [],
		projects: [],
		totalCount: 0,
		loading: false,
		showFilters: false,
		isAddTaskOpen: false,
		filters: {
			search: '',
			projects: null,
			taskType: null,
			workType: null,
			assignedTo: null,
			status: null,
			priority: null,
			submittedForReview: null,
		}
	});

	const updateState = (newState) => setState((prev) => ({ ...prev, ...newState }));
	const toggleFilters = () => updateState({ showFilters: !state.showFilters });

	const updateFilters = (newFilters) => setState((prev) => ({
		...prev,
		filters: { ...prev.filters, ...newFilters },
		page: 0
	}));

	const handleReset = () => {
		updateState({
			filters: {
				search: '',
				projects: null,
				taskType: null,
				workType: null,
				assignedTo: null,
				status: null,
				priority: null,
				submittedForReview: null
			}
		});
	};

	const fetchTasks = useCallback(async () => {
		updateState({ loading: true });
		try {
			const userRole = userData?.user_type;
			const isManager = userRole === 'Manager';

			const params = {
				page: state.page + 1,
				limit: state.limit,
				search: state.filters.search,
				project_id: state.filters.projects?.id || '',
				work_type: state.filters.workType || '',
				priority: state.filters.priority || '',
				status: state.filters.status || '',
				user_role: isManager ? '' : userRole,
				user_id: isManager ? '' : userId,
				assignee: state.filters.assignedTo?.id || '',
			};

			const url = API_ENDPOINTS.fetchAllTasks(params);
			const response = await getData(url);

			if (response?.status === "SUCCESS") {
				const dataContainer = response.result;

				updateState({
					tasks: dataContainer.tasks || [],
					totalCount: dataContainer.pagination?.totalItems || 0,
					loading: false
				});

				setStatCounts({
					total_task_count: dataContainer.stats?.totalTasks || 0,
					Open: dataContainer.stats?.todo || 0,
					'In Progress': dataContainer.stats?.inProgress || 0,
					Completed: dataContainer.stats?.completed || 0,
					Overdue: dataContainer.stats?.overdue || 0
				});
			} else {
				updateState({ loading: false, tasks: [] });
			}
		} catch (error) {
			updateState({ loading: false });
		}
		// Added userId and userData?.user_type to the dependency array
	}, [state.page, state.limit, state.filters, userId, userData?.user_type]);

	const fetchProjects = useCallback(async () => {
		if (!userId) return;
		try {
			const res = await getData(API_ENDPOINTS.fetchProjectsBySupervisor(userId));
			updateState({ projects: res.status === "SUCCESS" ? res.message.data : [] });
		} catch (err) {
			updateState({ projects: [] });
		}
	}, [userId]);

	const fetchEmployees = useCallback(async () => {
		if (!orgId) return;
		try {
			const response = await getData(API_ENDPOINTS.fetchEmployeesByOrganizationId(orgId));
			if (response?.status === "SUCCESS") {
				const data = response.message.employees || [];
				setEmployees(data);
			}
		} catch (error) {
			return null;
		}
	}, [orgId]);

	useEffect(() => {
		fetchTasks();
	}, [fetchTasks]);

	useEffect(() => {
		fetchProjects();
	}, [fetchProjects]);

	useEffect(() => {
		fetchEmployees();
	}, [fetchEmployees]);

	const handleOpenAddTask = () => updateState({ isAddTaskOpen: true });

	const handleCloseAddTask = (refresh = false) => {
		updateState({ isAddTaskOpen: false });
		if (refresh === true) {
			fetchTasks(); // Refresh list if a task was added
		}
	};

	return (
		<Box sx={{ p: 1 }}>
			{state.loading && <Loader />}

			<Stack direction="row" justifyContent="space-between" mb={3} alignItems="center">
				<Typography variant="h5" fontWeight="700" color="#1E293B">Tasks Overview</Typography>
				<Stack direction="row" spacing={3} alignItems="center">
					<Button
						variant="contained"
						startIcon={<AddIcon />}
						onClick={handleOpenAddTask}
						sx={{
							borderRadius: '8px',
							bgcolor: '#6366f1',
							textTransform: 'none',
							px: 3,
							'&:hover': { bgcolor: '#4f46e5' }
						}}
					>
						Add Task
					</Button>
				</Stack>
			</Stack>

			<Grid container spacing={3} mb={4}>
				<Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
					<StatCard title="Total Tasks" value={statCount?.total_task_count || 0} icon={<TaskIcon />} color="#6366F1" />
				</Grid>
				<Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
					<StatCard title="Todo" value={statCount?.Open || 0} icon={<OpenIcon />} color="#F59E0B" />
				</Grid>
				<Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
					<StatCard title="In Progress" value={statCount?.['In Progress'] || 0} icon={<ProgressIcon />} color="#3B82F6" />
				</Grid>
				<Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
					<StatCard title="Completed" value={statCount?.Completed || 0} icon={<CompletedIcon />} color="#10B981" />
				</Grid>
				<Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
					<StatCard title="Overdue" value={statCount?.Overdue || 0} icon={<Folder />} color="#EF4444" />
				</Grid>
			</Grid>

			<Grid container spacing={2} alignItems="center">
				<Grid size={{ xs: 12, md: 9 }}>
					<Autocomplete
						freeSolo // Allows users to type custom text not in the list
						disableClearable
						size="small"
						// Generate suggestions based on current tasks or projects
						options={Array.from(new Set([
							...state.tasks.map(t => t.task_title),
							...state.tasks.map(t => t.Project?.project_title).filter(Boolean)
						]))}
						value={state.filters.search}
						onInputChange={(event, newInputValue) => {
							updateFilters({ search: newInputValue });
						}}
						renderInput={(params) => (
							<TextField
								{...params}
								placeholder="Search by project name or task..."
								InputProps={{
									...params.InputProps,
									type: 'search',
									startAdornment: (
										<>
											<SearchIcon sx={{ color: 'gray', ml: 1, mr: 1, fontSize: 20 }} />
											{params.InputProps.startAdornment}
										</>
									),
								}}
							/>
						)}
					/>
				</Grid>
				<Grid size={{ xs: 12, md: 3 }} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
					<Button
						variant={state.showFilters ? "contained" : "outlined"}
						startIcon={state.showFilters ? <ExpandLessIcon /> : <FilterIcon />}
						onClick={toggleFilters}
						sx={{ borderRadius: '8px', textTransform: 'none' }}
					>
						{state.showFilters ? "Hide Filters" : "Show Filters"}
					</Button>
					<Button
						startIcon={<ResetIcon />}
						onClick={handleReset}
						sx={{ textTransform: 'none', color: 'text.secondary' }}
					>
						Reset
					</Button>
				</Grid>
				<Grid size={{ xs: 12 }}>
					<Collapse in={state.showFilters}>
						<Grid container spacing={2} sx={{ mt: 0.5, mb: 2, borderTop: '1px solid #f0f0f0' }}>
							<Grid size={{ xs: 12, sm: 6, md: 3 }}>
								<Autocomplete
									size="small" options={state.projects || []}
									getOptionLabel={(option) => option.project_title || ""}
									renderInput={(params) => <TextField {...params} label="Project" />}
									value={state.filters.projects}
									onChange={(_, val) => updateFilters({ projects: val })}
								/>
							</Grid>
							<Grid size={{ xs: 12, sm: 6, md: 3 }}>
								<Autocomplete
									size="small" options={['Bug', 'Enhancement', 'Feature']}
									renderInput={(params) => <TextField {...params} label="Work Type" />}
									value={state.filters.workType}
									onChange={(_, val) => updateFilters({ workType: val })}
								/>
							</Grid>
							<Grid size={{ xs: 12, sm: 6, md: 3 }}>
								<Autocomplete
									size="small" options={employees}
									getOptionLabel={(option) => option ? `${option.first_name} ${option.last_name}` : ""}
									renderInput={(params) => <TextField {...params} label="Assigned To" />}
									value={state.filters.assignedTo}
									onChange={(_, val) => updateFilters({ assignedTo: val })}
								/>
							</Grid>
							<Grid size={{ xs: 12, sm: 6, md: 3 }}>
								<Autocomplete
									size="small" options={['Todo', 'In Progress', 'Completed', 'Hold', 'Overdue']}
									getOptionLabel={(o) => o === 'Hold' ? 'On Hold' : o}
									renderInput={(params) => <TextField {...params} label="Status" />}
									value={state.filters.status}
									onChange={(_, val) => updateFilters({ status: val })}
								/>
							</Grid>
						</Grid>
					</Collapse>
				</Grid>
			</Grid>

			<TableContainer component={Paper} sx={{ borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.04)', border: '1px solid #E2E8F0' }}>
				<Table sx={{ minWidth: 1100 }}>
					<TableHead sx={{ bgcolor: '#EFF6FF' }}>
						<TableRow>
							<TableCell sx={{ color: '#1D4ED8', fontWeight: 700, width: 250 }}>Task Details</TableCell>
							<TableCell sx={{ color: '#1D4ED8', fontWeight: 700, width: 100 }}>Project</TableCell>
							<TableCell sx={{ color: '#1D4ED8', fontWeight: 700, width: 100 }}>Assignees</TableCell>
							<TableCell sx={{ color: '#1D4ED8', fontWeight: 700, width: 100 }}>Created Date</TableCell>
							<TableCell sx={{ color: '#1D4ED8', fontWeight: 700, width: 100 }}>Deadline</TableCell>
							<TableCell sx={{ color: '#1D4ED8', fontWeight: 700, width: 150 }} align="center">Effort</TableCell>
							<TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }}>Priority</TableCell>
							<TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }}>Status</TableCell>
							<TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }} align="center">Review</TableCell>
							<TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }} align="center">Action</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{state.tasks.length > 0 ? (
							state.tasks.map((task) => {
								const statusStyle = getStatusColor(task.status);
								return (
									<TableRow key={task.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
										<TableCell>
											<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
												<Typography
													variant="body2"
													fontWeight="600"
													color="#1e293b"
													sx={{ lineHeight: 1.2 }}
												>
													{task.task_title}
												</Typography>

												<Box sx={{ display: 'flex', alignItems: 'center' }}>
													<Typography variant="caption" color="textSecondary">
														{task.task_type}
													</Typography>

													{task.work_type && (
														<Chip
															label={task.work_type}
															size="small" // "extraSmall" is not a standard MUI prop
															sx={{
																ml: 1,
																height: '18px', // Manual override for "extra small" look
																fontSize: '0.6rem',
																fontWeight: 700,
																bgcolor: issueTypeColors[task.work_type]?.bg || '#f1f5f9',
																color: issueTypeColors[task.work_type]?.color || '#475569',
																'& .MuiChip-label': { px: 1 } // Reduce internal padding
															}}
														/>
													)}
												</Box>
											</Box>
										</TableCell>
										<TableCell>
											<Typography variant="body2" color="#475569">{task.Project?.project_title || "N/A"}</Typography>
										</TableCell>
										<TableCell align="center">
											<AvatarGroup
												max={3}
												sx={{
													'& .MuiAvatar-root': { width: 26, height: 26, fontSize: '0.75rem' },
													justifyContent: 'center'
												}}
											>
												{task.assignees?.map((a) => (
													<Tooltip arrow
														key={a.id || `${a.first_name}-${a.last_name}`}
														title={`${a.first_name} ${a.last_name}`}
													>
														<Avatar alt={`${a.first_name} ${a.last_name}`}>
															{`${a.first_name?.charAt(0) || ''}${a.last_name?.charAt(0) || ''}`}
														</Avatar>
													</Tooltip>
												))}
											</AvatarGroup>
										</TableCell>
										<TableCell>
											<Typography variant="body2">{formatDate(task.start_date)}</Typography>
										</TableCell>
										<TableCell>
											<Typography variant="body2">{formatDate(task.due_date)}</Typography>
										</TableCell>
										<TableCell align="start">
											<Box
												display="flex"
												flexDirection="column"
												justifyContent="start"
												alignItems="start"
											>
												<Typography variant="caption" fontWeight="500">
													Est. Hrs - <span style={{ color: '#64748b' }}>
														{task.estimated_hours ? `${task.estimated_hours}Hrs` : 'N/A'}
													</span>
												</Typography>

												<Typography variant="caption" fontWeight="500">
													Act. Hrs - <span style={{ color: '#64748b' }}>
														{task.actual_hours ? `${task.actual_hours}Hrs` : 'N/A'}
													</span>
												</Typography>
											</Box>
										</TableCell>
										<TableCell>
											<Chip
												label={task.priority} size="small"
												sx={{
													fontSize: '0.7rem',
													fontWeight: 700,
													bgcolor: priorityColors[task.priority]?.bg,
													color: priorityColors[task.priority]?.color,
													border: `1px solid ${priorityColors[task.priority]?.border || '#e2e8f0'}`
												}}
											/>
										</TableCell>
										<TableCell>
											<Chip
												label={task.status === 'Hold' ? 'On Hold' : task.status} size="small"
												sx={{
													fontWeight: 600,
													bgcolor: statusStyle.bg,
													color: statusStyle.color,
													border: `1px solid ${statusStyle.border || '#cbd5e1'}`
												}}
											/>
										</TableCell>
										<TableCell align="center">
											{task.submit_for_review ? (
												<Tooltip title="Submitted for Review" arrow>
													<RateReview sx={{ fontSize: 20, color: '#2b8a3e', cursor: 'help' }} />
												</Tooltip>
											) : (
												<Tooltip title="Pending Submission" arrow>
													<PendingActions sx={{ fontSize: 20, color: '#adb5bd', cursor: 'help' }} />
												</Tooltip>
											)}
										</TableCell>
										<TableCell align="center">
											<IconButton size="small" onClick={() => navigate(`${roleBasePath}/user-task-details?taskId=${task.id}`)}>
												<Visibility fontSize="small" />
											</IconButton>
										</TableCell>
									</TableRow>
								);
							})
						) : (
							<TableRow>
								<TableCell colSpan={9} align="center" sx={{ py: 8 }}>
									{state.loading ? <Loader /> : "No tasks found."}
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>

			<TablePagination
				component="div"
				count={state.totalCount}
				page={state.page}
				onPageChange={(_, newPage) => updateState({ page: newPage })}
				rowsPerPage={state.limit}
				onRowsPerPageChange={(e) => updateState({ limit: parseInt(e.target.value, 10), page: 0 })}
				rowsPerPageOptions={[5, 10, 25]}
			/>

			<Dialog
				open={state.isAddTaskOpen}
				onClose={(event, reason) => {
					if (reason === 'backdropClick') return;
					handleCloseAddTask(false);
				}}
				maxWidth="md"
				fullWidth
				PaperProps={{
					sx: { borderRadius: '16px' }
				}}
			>
				<DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography variant="h6" component="span" fontWeight="700">
						Create New Task
					</Typography>
					<IconButton onClick={() => handleCloseAddTask(false)} sx={{ color: 'grey.500' }}>
						<CloseIcon />
					</IconButton>
				</DialogTitle>

				<DialogContent dividers sx={{ border: 'none' }}>
					<CreateTask
						onClose={() => handleCloseAddTask(false)}
						onSuccess={() => handleCloseAddTask(true)}
						isTaskManagement={true}
					/>
				</DialogContent>
			</Dialog>
		</Box>
	);
}

export default TaskManagement;